import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CountriesService, Country, NationalityTaxAndEmploymentInformation, TinErrorPipe, TinPipe } from '@zero/onboarding/domain';
import { IconComponent, InputComponent, Item, SelectComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    SelectComponent,
    AsyncPipe,
    NgbTooltipModule,
    NgTemplateOutlet,
    InputComponent,
    TinPipe,
    IconComponent,
    TinErrorPipe,
  ],
  templateUrl: './nationality.component.html',
})
export class NationalityComponent extends BaseComponent implements OnInit {
  readonly #countriesService = inject(CountriesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #nationality = new BehaviorSubject<string>(undefined);
  readonly #taxCountry = new BehaviorSubject<string>(undefined);
  readonly #tin = new BehaviorSubject<string>(undefined);
  readonly #secondTaxCountry = new BehaviorSubject<string>(undefined);
  readonly #thirdTaxCountry = new BehaviorSubject<string>(undefined);
  readonly #secondTin = new BehaviorSubject<string>(undefined);
  readonly #thirdTin = new BehaviorSubject<string>(undefined);
  readonly #selfEmployed = new BehaviorSubject<number>(undefined);
  readonly #branch = new BehaviorSubject<string>(undefined);
  readonly #isSecondTaxCountryVisible = new BehaviorSubject<boolean>(false);
  readonly #isThirdTaxCountryVisible = new BehaviorSubject<boolean>(false);
  readonly #shouldEnterTinIndependently = new BehaviorSubject<boolean>(false);
  readonly #tinPipe = new TinPipe(this.#countriesService);

  // TODO: get rid of this bullshit please
  readonly #selfEmployedOptions = new BehaviorSubject<Item[]>([
    { value: 1, label: 'Selbstständig' },
    { value: 2, label: 'Angestellt' },
    { value: 3, label: 'Student' },
    { value: 4, label: 'Arbeitslos' },
    { value: 5, label: 'Sonstiges' },
  ]);
  readonly #nationalities = new BehaviorSubject<Item[]>([]);
  readonly #taxCountries = new BehaviorSubject<Item[]>([]);
  readonly #taxCountries2 = new BehaviorSubject<Item[]>([]);
  readonly #taxCountries3 = new BehaviorSubject<Item[]>([]);
  readonly #branches = new BehaviorSubject<Item[]>([]);

  public readonly nationality$ = this.#nationality.asObservable();
  public readonly tin$ = this.#tin.asObservable();
  public readonly taxCountry$ = this.#taxCountry.asObservable();
  public readonly secondTaxCountry$ = this.#secondTaxCountry.asObservable();
  public readonly secondTin$ = this.#secondTin.asObservable();
  public readonly thirdTaxCountry$ = this.#thirdTaxCountry.asObservable();
  public readonly thirdTin$ = this.#thirdTin.asObservable();
  public readonly isTaxCountryGermany$ = this.#taxCountry.pipe(map((country) => country === 'DE'));
  public readonly selfEmployed$ = this.#selfEmployed.asObservable();
  public readonly branch$ = this.#branch.asObservable();
  public readonly nationalities$ = this.#nationalities.asObservable();
  public readonly taxCountries$ = this.#taxCountries.asObservable();
  public readonly taxCountries2$ = this.#taxCountries2.asObservable();
  public readonly taxCountries3$ = this.#taxCountries3.asObservable();
  public readonly selfEmployedOptions$ = this.#selfEmployedOptions.asObservable();
  public readonly branches$ = this.#branches.asObservable();
  public readonly isSecondTaxCountryVisible$ = this.#isSecondTaxCountryVisible.asObservable();
  public readonly isThirdTaxCountryVisible$ = this.#isThirdTaxCountryVisible.asObservable();
  public readonly shouldEnterTinIndependently$ = this.#shouldEnterTinIndependently.asObservable();
  public readonly isSecondTaxCountryUSA$ = this.#secondTaxCountry.pipe(map((country) => country === 'US'));
  public readonly isThirdTaxCountryUSA$ = this.#thirdTaxCountry.pipe(map((country) => country === 'US'));

  public formValidator = ([
    nationality,
    taxCountry,
    isTaxCountryGermany,
    tin,
    secondTaxCountry,
    secondTin,
    thirdTaxCountry,
    thirdTin,
    selfEmployed,
    branch,
    shouldEnterTinIndependently,
    isSecondTaxCountryVisible,
    isThirdTaxCountryVisible,
    sendingNationalityTaxAndEmploymentInformation,
  ]) => {
    return (
      this.#validateBasicFields(nationality, taxCountry, selfEmployed, sendingNationalityTaxAndEmploymentInformation) &&
      this.#validateTin(isTaxCountryGermany, shouldEnterTinIndependently, tin, taxCountry) &&
      this.#validateSecondTaxCountry(isSecondTaxCountryVisible, secondTaxCountry, secondTin) &&
      this.#validateThirdTaxCountry(isThirdTaxCountryVisible, thirdTaxCountry, thirdTin) &&
      this.#validateSelfEmployed(selfEmployed, branch)
    );
  };

  #validateBasicFields(
    nationality: string,
    taxCountry: string,
    selfEmployed: number,
    sendingNationalityTaxAndEmploymentInformation: boolean,
  ): boolean {
    return !!nationality && !!taxCountry && !!selfEmployed && !sendingNationalityTaxAndEmploymentInformation;
  }

  #validateTin(isTaxCountryGermany: boolean, shouldEnterTinIndependently: boolean, tin: string, taxCountry: string): boolean {
    const isValidTin = this.#tinPipe.transform(tin, taxCountry);
    if (isTaxCountryGermany && shouldEnterTinIndependently && !isValidTin) {
      return false;
    }
    if (!isTaxCountryGermany && !isValidTin) {
      return false;
    }
    return true;
  }

  #validateSecondTaxCountry(isSecondTaxCountryVisible: boolean, secondTaxCountry: string, secondTin: string): boolean {
    if (isSecondTaxCountryVisible && !secondTaxCountry) {
      return false;
    }
    if (secondTaxCountry) {
      const isValidSecondTin = this.#tinPipe.transform(secondTin, secondTaxCountry) && secondTaxCountry !== 'US';
      if (!isValidSecondTin) {
        return false;
      }
    }
    return true;
  }

  #validateThirdTaxCountry(isThirdTaxCountryVisible: boolean, thirdTaxCountry: string, thirdTin: string): boolean {
    if (isThirdTaxCountryVisible && !thirdTaxCountry) {
      return false;
    }
    if (thirdTaxCountry) {
      const isValidThirdTin = this.#tinPipe.transform(thirdTin, thirdTaxCountry) && thirdTaxCountry !== 'US';
      if (!isValidThirdTin) {
        return false;
      }
    }
    return true;
  }

  #validateSelfEmployed(selfEmployed: number, branch: string): boolean {
    if (selfEmployed === 1 && !branch) {
      return false;
    }
    return true;
  }

  public readonly isFormValid$ = combineLatest([
    this.nationality$,
    this.taxCountry$,
    this.isTaxCountryGermany$,
    this.tin$,
    this.secondTaxCountry$,
    this.secondTin$,
    this.thirdTaxCountry$,
    this.thirdTin$,
    this.selfEmployed$,
    this.branch$,
    this.shouldEnterTinIndependently$,
    this.isSecondTaxCountryVisible$,
    this.isThirdTaxCountryVisible$,
    this.facade.sendingNationalityTaxAndEmploymentInformation$,
  ]).pipe(map(this.formValidator));

  public readonly taxCountryName$ = this.taxCountries$.pipe(
    map((countries) => countries.find((country) => country.value === this.#taxCountry.value)),
    map((country) => country?.label),
  );

  override ngOnInit(): void {
    super.ngOnInit();
    this.facade.loadBranches();
  }

  protected override populateFromData(): void {
    combineLatest([this.facade.onboardingData$, this.facade.branches$, this.facade.countries$, this.facade.validTaxResidences$])
      .pipe(
        filter(
          ([data, branches, countries, validTaxResidences]) => !!data && !!branches && !!countries?.length && !!validTaxResidences?.length,
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(
        ([
          { branche, selbstaendig, steuerid, steuerid2, steuerid3, steuerland, steuerland2, steuerland3, nationality },
          branches,
          countries,
          validTaxResidences,
        ]) => {
          this.#setNationality(countries, nationality);
          this.#setTaxCountry(validTaxResidences, steuerland, steuerid);
          steuerid && this.#isSecondTaxCountryVisible.next(true);
          this.#setSecondTaxCountry(countries, steuerland2, steuerid2);
          steuerid2 && this.#isThirdTaxCountryVisible.next(true);
          this.#setThirdTaxCountry(countries, steuerland3, steuerid3);
          this.#branches.next(branches.map((branch) => ({ value: branch.value, label: branch.label, selected: branch.value === branche })));
          this.#branch.next(branche);
          // dirty hack to make sure the selfEmployed value is set correctly
          const selfEmployed = selbstaendig ? 1 : 2;
          this.#selfEmployed.next(selfEmployed);
          this.#selfEmployedOptions.next([
            ...this.#selfEmployedOptions.value.map((option) => ({
              ...option,
              selected: option.value === selfEmployed,
            })),
          ]);
        },
      );
  }

  protected override listenToSuccessfulSubmit(): void {
    this.facade.sentNationalityTaxAndEmploymentInformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) &&
      this.facade.sendNationalityTaxAndEmploymentInformation({
        taxCountry: this.#taxCountry.value,
        tin: this.#tin.value,
        secondTaxCountry: this.#secondTaxCountry.value,
        secondTin: this.#secondTin.value,
        nationality: this.#nationality.value,
        selfEmployed: this.#selfEmployed.value === 1,
        branch: this.#selfEmployed.value === 1 ? this.#branch.value : null,
      } as NationalityTaxAndEmploymentInformation);
  }

  public selectNationality = (item: Item) => this.#nationality.next(item.value as string);
  public selectTaxCountry = (item: Item) => this.#taxCountry.next(item.value as string);
  public selectSecondTaxCountry = (item: Item) => this.#secondTaxCountry.next(item.value as string);
  public selectThirdTaxCountry = (item: Item) => this.#thirdTaxCountry.next(item.value as string);
  public handleTinChange = (tin: string) => this.#tin.next(tin);
  public handleSecondTinChange = (tin: string) => this.#secondTin.next(tin);
  public handleThirdTinChange = (tin: string) => this.#thirdTin.next(tin);
  public addSecondTaxCountry = () =>
    this.#isSecondTaxCountryVisible.next(Boolean(!this.#isSecondTaxCountryVisible.value && this.#taxCountry.value));
  public addThirdTaxCountry = () => this.#isThirdTaxCountryVisible.next(true);

  public removeSecondTaxCountry() {
    this.#isSecondTaxCountryVisible.next(false);
    this.#secondTaxCountry.next(undefined);
    this.#secondTin.next(undefined);
  }
  public removeThirdTaxCountry() {
    this.#isThirdTaxCountryVisible.next(false);
    this.#thirdTaxCountry.next(undefined);
    this.#thirdTin.next(undefined);
  }
  public selectSelfEmployed = (item: Item) => {
    !item.value && this.#branch.next(undefined);
    this.#selfEmployed.next(item.value === 1 ? 1 : 2);
  };
  public selectBranch = (item: Item) => this.#branch.next(item.value as string);
  public setIndependentlyEnterTin = (independently: boolean) => this.#shouldEnterTinIndependently.next(independently);

  readonly #countryItemMapper = (selectedCountryCode: string, country: Country): Item => ({
    value: country.isoCode,
    label: country.fullName,
    selected: country.isoCode === selectedCountryCode,
  });

  #setNationality(nationalities: Country[], nationality: string) {
    this.#nationalities.next(
      nationalities.map(
        (country: Country, index: number): Item => ({
          value: country.isoCode,
          label: country.fullName,
          selected: !nationality ? index === 0 : country.isoCode === nationality,
        }),
      ),
    );
    this.#nationality.next(nationality || (this.#nationalities.value[0]?.value?.toString() ?? ''));
  }

  #setTaxCountry(validTaxCountries: Country[], taxCountry: string, taxId: string) {
    this.#taxCountries.next(
      validTaxCountries.map(
        (country: Country, index: number): Item => ({
          value: country.isoCode,
          label: country.fullName,
          selected: !taxCountry ? index === 0 : country.isoCode === taxCountry,
        }),
      ),
    );
    this.#taxCountry.next(taxCountry || (this.#taxCountries.value[0]?.value?.toString() ?? ''));
    this.#tin.next(taxId);
  }

  #setSecondTaxCountry(countries: Country[], secondTaxCountry: string, taxId: string) {
    taxId && this.#secondTin.next(taxId);
    secondTaxCountry && this.#secondTaxCountry.next(secondTaxCountry);
    this.#taxCountries2.next([...countries.map((country) => this.#countryItemMapper(secondTaxCountry, country))]);
  }

  #setThirdTaxCountry(countries: Country[], thirdTaxCountry: string, taxId: string) {
    taxId && this.#thirdTin.next(taxId);
    thirdTaxCountry && this.#thirdTaxCountry.next(thirdTaxCountry);
    this.#taxCountries3.next([...countries.map((country) => this.#countryItemMapper(thirdTaxCountry, country))]);
  }
}
